import { useCallback, useEffect, useState } from "react";
import { fetchApprovalDetail } from "@/lib/api";
import type { ApprovalDetail, ApprovalStatus } from "@/types";

interface UseApprovalDetailResult {
  detail: ApprovalDetail | null;
  status: ApprovalStatus;
  receiptRef: string | null;
  loading: boolean;
  error: string | null;
  reasonCode: string | null;
  source: "api";
  refresh: () => void;
}

function toApprovalStatus(raw: unknown): ApprovalStatus {
  const value = String(raw ?? "pending").toLowerCase();
  if (value.includes("approved")) return "approved";
  if (value.includes("reject")) return "rejected";
  if (value.includes("escalat")) return "escalated";
  return "pending";
}

// Maps the real gateway response (GatewayApprovalResponse in
// alloy-api-gateway-service/.../ApprovalController.java) to the UI's
// ApprovalDetail shape. The gateway does not return policy description,
// screening result, or a per-approver roster — only a policy *name* and
// required/current approval counts. Fields with no backend source are left
// empty/absent rather than invented (ALL-4734 G6); the UI renders those
// sections conditionally instead of showing fabricated copy.
function mapApiApprovalDetail(raw: Record<string, unknown>): ApprovalDetail {
  const id = String(raw.id ?? "");
  // `||`, not `??`: an empty string is "no value" for these identity fields, so
  // a present-but-empty transferId must not shadow a populated linkedResourceId
  // (and likewise requesters below). The page renders both, so an empty
  // primary value would otherwise blank a link that the gateway did send.
  const transferId = String(raw.transferId || raw.linkedResourceId || "");
  const transferSummary = (raw.transferSummary ?? {}) as Record<string, unknown>;
  const amount = transferSummary.amount != null && transferSummary.asset != null
    ? `${transferSummary.amount} ${transferSummary.asset}`
    : String(transferSummary.amount ?? "");
  const source = String(transferSummary.sourceWallet ?? transferSummary.source ?? "");
  const destination = String(transferSummary.destination ?? "");
  const requestedBy = (raw.requestedBy ?? {}) as Record<string, unknown>;
  const requester = String(requestedBy.name || requestedBy.id || "");
  const risk = (raw.risk === "high" ? "high" : "low") as ApprovalDetail["risk"];
  const policyTitle = String(raw.policyName ?? raw.policyTitle ?? "");
  const requiredApprovals = typeof raw.requiredApprovals === "number" ? raw.requiredApprovals : 0;
  const currentApprovals = typeof raw.currentApprovals === "number" ? raw.currentApprovals : 0;
  const quorumLabel = requiredApprovals > 0 ? `Required: ${currentApprovals} of ${requiredApprovals}` : "";
  const quorumPct = requiredApprovals > 0 ? Math.round((currentApprovals / requiredApprovals) * 100) : 0;

  return {
    id,
    approvalId: id,
    transferId,
    amount,
    source,
    destination,
    requester,
    memo: "",
    risk,
    policyTitle,
    policyDesc: "",
    screeningTitle: "",
    screeningDesc: "",
    quorumLabel,
    quorumPct,
    approvers: [],
  };
}

export function useApprovalDetail(id: string): UseApprovalDetailResult {
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);
  const [status, setStatus] = useState<ApprovalStatus>("pending");
  const [receiptRef, setReceiptRef] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReasonCode(null);
    try {
      const result = await fetchApprovalDetail(id);
      if (result.approval) {
        setDetail(mapApiApprovalDetail(result.approval));
        setStatus(toApprovalStatus(result.approval.status));
        setReceiptRef(result.approval.receiptRef ? String(result.approval.receiptRef) : null);
      } else {
        setError(result.reasonCode || "Approval not found");
        setReasonCode(result.reasonCode ?? null);
        setDetail(null);
      }
    } catch {
      setError("Failed to load approval");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return { detail, status, receiptRef, loading, error, reasonCode, source: "api", refresh: load };
}
